// The strip under every editor's header: where this record stands in the working language, and what to do next

import type { ReactNode } from "react"
import { Button } from "@/components/ui/button"
import { CheckCircle2, Circle, Copy } from "lucide-react"
import { STATUS_LABEL, type LanguageInfo, type TranslationStatus } from "../../languages"
import { StatusDot } from "./StatusDot"

export interface TranslationStatusBarProps {
  language: LanguageInfo
  source: LanguageInfo
  status: TranslationStatus
  // Omit where review isn't tracked
  onToggleReviewed?: () => void
  // Fills the language with the source text, as a starting point for translating
  onStartFromSource?: () => void
  // The language isn't offered here yet: why, and the button that fixes it
  notListed?: { message: string; action: string; onAdd?: () => void }
  readOnly?: boolean
  // Why readers never see this language (a code the sites can't show); replaces notListed
  unsupported?: string
  // Section-specific controls, e.g. publishing this language
  children?: ReactNode
}

export function TranslationStatusBar({
  language, source, status, onToggleReviewed, onStartFromSource, notListed, readOnly, unsupported, children,
}: TranslationStatusBarProps) {
  const hint =
    status === "source" ? `Source language: other languages fall back to this text.`
      : status === "missing" ? `No ${language.name} text yet. Empty fields show the ${source.name} source.`
        : status === "unreviewed" ? "Not checked by a person yet."
          : status === "reviewed" ? "Checked by a person."
            : `Shown to ${language.name} readers.`

  return (
    <div className="border-b bg-muted/30">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-1.5">
        <span className="flex min-w-0 items-center gap-1.5 text-xs">
          <StatusDot status={status} />
          <span className="font-medium">{language.name}</span>
          <span className="text-muted-foreground">· {STATUS_LABEL[status]}</span>
        </span>
        <span className="min-w-0 flex-1 truncate text-[11px] text-muted-foreground" title={hint}>{hint}</span>
        <span className="flex shrink-0 items-center gap-1.5">
          {children}
          {!readOnly && status === "missing" && onStartFromSource && (
            <Button variant="outline" size="sm" className="h-7 gap-1 text-xs" onClick={onStartFromSource}
              title={`Copy the ${source.name} text into ${language.name} to translate it in place. It stays unreviewed.`}>
              <Copy className="h-3 w-3" /> Start from {source.name}
            </Button>
          )}
          {!readOnly && onToggleReviewed && (status === "unreviewed" || status === "reviewed") && (
            <Button
              variant={status === "reviewed" ? "ghost" : "outline"} size="sm"
              className="h-7 gap-1 text-xs"
              onClick={onToggleReviewed}
              title={status === "reviewed" ? "Mark as not reviewed" : `Mark the ${language.name} text as checked by a person`}
            >
              {status === "reviewed"
                ? <><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Reviewed</>
                : <><Circle className="h-3.5 w-3.5" /> Mark reviewed</>}
            </Button>
          )}
        </span>
      </div>
      {unsupported && (
        <div className="border-t border-rose-200 bg-rose-50 px-3 py-1.5 text-[11px] text-rose-800 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300">
          {unsupported} Readers never see text in this code. Move it with Manage languages → Copy wording.
        </div>
      )}
      {notListed && !unsupported && (
        <div className="flex items-center gap-2 border-t border-amber-200 bg-amber-50 px-3 py-1.5 text-[11px] text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300">
          <span className="flex-1">{notListed.message}</span>
          {notListed.onAdd && !readOnly && (
            <Button variant="outline" size="sm" className="h-6 border-amber-300 bg-transparent px-2 text-[11px]" onClick={notListed.onAdd}>
              {notListed.action}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
